"use client";

import Image, { StaticImageData } from "next/image";
import { PortableText } from "@portabletext/react";
import { PortableTextBlock } from "sanity";

import { GROQButton } from "@/sanity/lib/definitions";
import { customRichTextComponent } from "@/sanity/sanityPortableText";
import { CTAWrapper } from "@/components/global/utils/CTAWrapper";
import { FadeIn } from "@/components/global/utils/FadeIn";
import { cn } from "@/lib/utils";

import HeroCountdown from "../optIn/HeroCountdown";

import iconCalendar from "@/public/optIn/icon-calendar.svg";
import iconYoutube from "@/public/optIn/icon-youtube.svg";

interface MessageSectionProps {
  title?: PortableTextBlock[];
  primaryCta?: GROQButton;
  secondaryCta?: GROQButton;
  eventDate?: string;
  eventDateText?: string;
}

const COUNTDOWN_LABELS = ["day", "hours", "minutes", "seconds"] as const;

// Confirmation message (Figma 5269:17175 desktop, 5242:17118 mobile) — serif
// headline (em → italic) and body, the two action buttons (calendar "Save Your
// Spot" and the glass "Youtube live"), then the white countdown card from the
// Opt-In page, centred in one column that sits mid-page on desktop. Headline →
// body spacing is a margin because the richtext renders them as siblings. The
// column stays full width so the wide headline never overflows it (an
// overflowing flex child drops its auto margins and pins left). Everything is
// on screen at load, so the reveals play on mount instead of waiting for in-view.
export default function MessageSection({
  title,
  primaryCta,
  secondaryCta,
  eventDate,
  eventDateText,
}: MessageSectionProps) {
  const hasCtas = Boolean(primaryCta?.cta || secondaryCta?.cta);

  return (
    <section className="relative z-10 flex w-full flex-col items-center px-[2rem] pt-[6.5rem] lg:flex-1 lg:justify-center lg:px-0 lg:py-[3rem]">
      <div className="flex w-full flex-col items-center gap-[2rem] lg:gap-[3rem]">
        {title && (
          <FadeIn
            immediate
            delay={0}
            className="flex w-full flex-col items-center">
            <PortableText
              value={title}
              components={customRichTextComponent({
                block: {
                  h1: ({ children }) => (
                    <h1 className="m-0 mx-auto w-full text-center font-news text-[4.75rem] leading-[1.06] font-normal tracking-[-0.0475rem] text-white lg:max-w-[82.5rem] lg:text-[7rem] lg:leading-[1.1] lg:tracking-[-0.07rem]">
                      {children}
                    </h1>
                  ),
                  normal: ({ children }) => (
                    <p className="mx-auto mb-[3rem] w-full max-w-[45.875rem] text-center font-geist text-[2.25rem] leading-[1.4] tracking-[-0.0225rem] text-white/80 [text-wrap:pretty] last:mb-0 lg:max-w-[48.5rem]">
                      {children}
                    </p>
                  ),
                },
                marks: {
                  em: ({ children }) => <em className="font-news italic">{children}</em>,
                  strong: ({ children }) => <strong className="font-normal text-white">{children}</strong>,
                },
              })}
            />
          </FadeIn>
        )}

        {hasCtas && (
          <FadeIn
            immediate
            delay={0.1}
            className="flex w-full flex-col items-stretch gap-[1.5rem] lg:w-auto lg:flex-row lg:items-start">
            {primaryCta?.cta && (
              <ActionButton
                cta={primaryCta}
                icon={iconCalendar}
                tone="primary"
              />
            )}
            {secondaryCta?.cta && (
              <ActionButton
                cta={secondaryCta}
                icon={iconYoutube}
                tone="glass"
              />
            )}
          </FadeIn>
        )}

        <FadeIn
          immediate
          delay={0.2}
          className="w-full lg:w-[71.75rem]">
          <HeroCountdown
            date={eventDate}
            caption={eventDateText}
            labels={COUNTDOWN_LABELS}
          />
        </FadeIn>
      </div>
    </section>
  );
}

// Pill button (Figma 5271:17224 / 5271:17226): 23px glyph, Geist Medium label.
// "primary" is the brand light-blue fill; "glass" the translucent bordered one.
function ActionButton({ cta, icon, tone }: { cta: GROQButton; icon: StaticImageData; tone: "primary" | "glass" }) {
  return (
    <CTAWrapper
      cta={cta.cta}
      className="w-full lg:w-auto">
      <span
        className={cn(
          "flex w-full items-center justify-center gap-[1.25rem] rounded-[1.25rem] px-[6rem] py-[1.75rem] font-geist text-[2rem] font-medium capitalize leading-[1.5] tracking-[-0.1rem] whitespace-nowrap transition-colors duration-200",
          tone === "primary"
            ? "bg-[#98D8FF] text-black hover:bg-[#b3e2ff]"
            : "bg-white/10 text-white ring-1 ring-inset ring-white/10 backdrop-blur-[12.5rem] hover:bg-white/20 lg:bg-white/[0.14]",
        )}>
        <span className="flex h-[2.875rem] w-[2.875rem] shrink-0 items-center justify-center">
          <Image
            src={icon}
            alt=""
            className="h-auto w-[2.5rem]"
          />
        </span>
        {cta.cta?.title}
      </span>
    </CTAWrapper>
  );
}
